/**
 * `model-read` - one model call that reads the receipt: the blocks in, the
 * asks out.
 *
 * Options: `in`, the blocks (`image`, `ocr-text`, `lines`, `company`,
 * `categories`); `ask`, the asks (`header`, `items` or `item-refs`,
 * `receipt-category`, `item-categories`); `prompt`, `compact` for production's
 * compact instructions (`RECEIPT_SYSTEM_PROMPT_COMPACT`, the one the server's
 * worker gives small local models) in place of the full ones - for the
 * `header` and `items` asks, and never on a correcting read, which needs the
 * reasoning the compact text leaves out. Needs the `transport` port and what
 * its blocks need. Provides `answer:<id>` and the candidates its asks find.
 * Leaves `categoriesDropped` in the facts when the category list did not fit.
 */

import { ASKS, parseAnswer, type AskName } from '../prompt/asks.js';
import { BLOCKS, type BlockName } from '../prompt/blocks.js';
import { buildReadPrompt, type BuiltPrompt } from '../prompt/compose.js';
import type { CorrectionContext } from '../../prompt.js';
import type { Key, ModelTransport, ReadingState, Step } from '../types.js';
import { lineTexts, summarize, transportOf } from './support.js';

export interface ModelReadOptions {
  in: BlockName[];
  ask: AskName[];
  prompt?: 'full' | 'compact';
}

export function readNeeds(options: ModelReadOptions): Key[] {
  return ['transport', ...options.in.flatMap((name) => BLOCKS[name].needs)];
}

export function readProvides(options: ModelReadOptions, id: string): Key[] {
  return [`answer:${id}`, ...options.ask.flatMap((name) => ASKS[name].provides.map((field) => `candidates:${field}`))];
}

/** The step's prompt for the state, as a model step builds it. */
export function readPrompt(
  state: Readonly<ReadingState>,
  transport: ModelTransport,
  options: ModelReadOptions,
  correction: CorrectionContext | null = null,
): BuiltPrompt {
  return buildReadPrompt({
    blocks: options.in,
    asks: options.ask,
    image: transport.images ? (state.input.image ?? null) : null,
    text: state.input.text ?? null,
    lines: lineTexts(state),
    ocrLines: state.input.lines ?? [],
    company: state.company,
    categories: state.input.categories,
    instructions: state.input.instructions,
    correction,
    compact: options.prompt === 'compact',
    inputBudget: transport.inputBudget,
  });
}

export const modelReadStep: Step<ModelReadOptions> = {
  name: 'model-read',
  needs: readNeeds,
  provides: readProvides,
  async run(state, context) {
    const transport = transportOf(context.ports);
    const prompt = readPrompt(state, transport, context.options);
    const response = await transport.send({ ...prompt, schemaName: 'receipt' });
    const candidates = parseAnswer(response.raw, context.id, context.options.ask);
    const files = context.options.ask.some((name) => ASKS[name].files);
    return {
      answer: response.raw,
      candidates,
      facts: { categoriesDropped: files && prompt.dropped.includes('categories') },
      dropped: prompt.dropped,
      tokens: { input: response.inputTokens, output: response.outputTokens },
      added: summarize(candidates),
    };
  },
};
